import { FC, createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { AUTH_STATUS, IRole } from 'src/constants';
import { TUserInfo } from 'src/types';

import eplSession from 'src/apis/eplSession';
export interface AuthContextType {
  status: AUTH_STATUS;
  isLoggedIn: boolean;
  userInfo?: TUserInfo;
  firstName: string;
  lastName: string;
  userName: string;
  userEmail: string;
  isAdmin: boolean;
  roles: IRole[];
}

export const AuthContext = createContext<AuthContextType>(undefined!);

interface AuthProviderProps {
  children: Nullable<ReactNode>;
}

interface IUser {
  userInfo?: any;
  isLoggedIn: boolean;
  status: AUTH_STATUS;
  roles: IRole[];
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {

  const [user, setUser] = useState<IUser>({
    userInfo: undefined,
    isLoggedIn: false,
    status: AUTH_STATUS.UNDETERMINED,
    roles: []
  });

  const authenticateCurrentUser = useCallback(async () => {
    try {
      const userDataFromEplSession = await eplSession.checkLogin();
      setUser(userDataFromEplSession);
    } catch (error) {
      setUser({
        ...user,
        status: AUTH_STATUS.LOGGED_OUT,
        isLoggedIn: false
      });
    }
  }, []);

  eplSession.onLogout(() => {
    setUser({
      ...user,
      status: AUTH_STATUS.LOGGED_OUT,
      isLoggedIn: false
    });
  });

  useEffect(() => {
    authenticateCurrentUser();
  }, [authenticateCurrentUser]);

  const firstName = user.userInfo?.['firstName'] ?? '';
  const lastName = user.userInfo?.['lastName'] ?? '';
  const userName = user.userInfo?.['cognito:username'] ?? '';
  const userEmail = user.userInfo?.['email'] ?? '';
  const isAdmin = user.userInfo?.['cognito:groups']?.includes('admin') ?? false;

  return (
    <AuthContext.Provider
      value={{
        ...user,
        firstName,
        lastName,
        userName,
        userEmail,
        isAdmin
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
